import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useSetting } from '../utils/AppSettingsContext';
import { getAssetSystems } from '../api/asset-systemAPI';
import { getEventLocations } from '../api/event-locationAPI';
import { useHasPageAccess } from '../masterLayout/MasterLayoutBody';
import { getTechnicians } from '../api/techniciansAPI';
import CreateDisplayOnlyUserModal from './child/CreateDisplayOnlyUserModal';
import EventCommentsSection from './EventCommentsSection';
import { useValidator } from '../utils/useValidator';

const ADD_CREW_MEMBER_OPTION = Object.freeze({
  value: '__add_new__',
  label: '+ Add New Crew Member',
  isAddNew: true,
});

const parseNumericValue = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const roundDateToNearestFiveMinutes = (value = new Date()) => {
  const fiveMinutesMs = 5 * 60 * 1000;
  const timestamp = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }
  return new Date(Math.round(timestamp / fiveMinutesMs) * fiveMinutesMs);
};

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
};

const areErrorObjectsEqual = (a = {}, b = {}) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every((key) => a[key] === b[key]);
};

const EventEditModal = ({
  show,
  onClose,
  onSave,
  editingEvent,
  newEvent,
  setNewEvent,
  newEventErrors = {},
  statusOptions = [],
  eventTypeOptions = [],
  eventResponsibilityOptions = [],
  showEventResponsibility = false,
  assetOptions = [],
  handleAssetSearch,
  disableAssetSelection = false,
  assignedWorkOrders = [],
  workOrderStatuses = [],
  onCompleteAllWork,
  onOpenAssignModal,
  onOpenAddWorkOrder,
  onEditWorkOrder,
  onWorkOrderStatusChange,
  onDeleteWorkOrder,
  eventDelays = [],
  onAddDelay,
  onEditDelay,
  onDeleteDelay,
  formatDateTime,
  eventComments = [],
  onEventCommentsChange,
}) => {
  const [systemOptions, setSystemOptions] = useState([]);
  const [eventLocationOptions, setEventLocationOptions] = useState([]);
  const [selectedAssetOption, setSelectedAssetOption] = useState(null);
  const [crewOptions, setCrewOptions] = useState([ADD_CREW_MEMBER_OPTION]);
  const [customCrewOptions, setCustomCrewOptions] = useState([]);
  const [showCreateCrewModal, setShowCreateCrewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const statusName = useMemo(() => {
    const selectedStatus = statusOptions.find(
      (s) => String(s.value) === String(newEvent?.eventStatus)
    );
    return selectedStatus?.label?.toLowerCase();
  }, [newEvent?.eventStatus, statusOptions]);
  const eventTypeName = useMemo(() => {
    const selectedType = eventTypeOptions.find(
      (t) => String(t.value) === String(newEvent?.eventTypeId)
    );
    return selectedType?.label?.toLowerCase();
  }, [eventTypeOptions, newEvent?.eventTypeId]);
  const hasPageAccess = useHasPageAccess();

  const eventSystemEnabled = useSetting('event_system');
  const eventTechsEnabled = useSetting('event_techs');
  const eventCommentActionsRef = useRef(null);

  const eventTypeId = parseNumericValue(newEvent?.eventTypeId);
  const eventStatusId = parseNumericValue(newEvent?.eventStatus);
  const isBreakdownEvent = eventTypeId === 2;
  const isPendingStatus = eventStatusId === 4 || statusName === 'pending';
  const isInProgressStatus = eventStatusId === 1 || statusName === 'in progress';
  const isCompleteStatus = statusName === 'complete';
  const isCancelledStatus =
    statusName === 'cancelled' || eventTypeName === 'cancelled';
  const isAssignableStatus =
    !editingEvent || isPendingStatus || isInProgressStatus;
  const showWorkOrderSection =
    hasPageAccess('/work-orders') &&
    (editingEvent || assignedWorkOrders.length > 0 || (onOpenAssignModal && isAssignableStatus));

  const {
    errors: validationErrors,
    setErrors: setValidationErrors,
    resetErrors: resetValidationErrors,
  } = useValidator();

  const runDateValidations = useCallback(() => {
    const nextErrors = {};

    const appendError = (field, message) => {
      if (!field || !message) return;
      nextErrors[field] = nextErrors[field] ? `${nextErrors[field]} ${message}` : message;
    };

    const plannedStart = parseDateValue(newEvent?.plannedStartDate);
    const plannedEnd = parseDateValue(newEvent?.plannedEndDate);
    const actualStart = parseDateValue(newEvent?.startDate);
    const actualEnd = parseDateValue(newEvent?.endDate);
    const now = new Date();

    if (plannedStart && plannedEnd && plannedStart >= plannedEnd) {
      const message = 'Planned start date must be before planned end date.';
      appendError('plannedStartDate', message);
      appendError('plannedEndDate', message);
    }

    if (actualStart && actualEnd && actualStart >= actualEnd) {
      const message = 'Start date must be before end date.';
      appendError('startDate', message);
      appendError('endDate', message);
    }

    if (actualStart && actualStart >= now) {
      appendError('startDate', 'Start date must be before the current time.');
    }

    if (actualEnd && actualEnd > now) {
      appendError('endDate', 'End date cannot be in the future.');
    }

    if (isInProgressStatus && actualStart && plannedEnd && actualStart >= plannedEnd) {
      const message = 'Start date must be before planned end date when event is in progress.';
      appendError('startDate', message);
      appendError('plannedEndDate', message);
    }

    setValidationErrors((prev) => (areErrorObjectsEqual(prev, nextErrors) ? prev : nextErrors));
    return nextErrors;
  }, [
    isInProgressStatus,
    newEvent?.endDate,
    newEvent?.plannedEndDate,
    newEvent?.plannedStartDate,
    newEvent?.startDate,
    setValidationErrors,
  ]);

  useEffect(() => {
    if (!show) {
      resetValidationErrors();
      return;
    }
    runDateValidations();
  }, [resetValidationErrors, runDateValidations, show]);

  const combinedErrors = useMemo(
    () => ({ ...(newEventErrors || {}), ...(validationErrors || {}) }),
    [newEventErrors, validationErrors],
  );

  const filteredStatusOptions = useMemo(() => {
    const options = Array.isArray(statusOptions) ? statusOptions : [];
    if (!isBreakdownEvent) return options;
    return options.filter((opt) => String(opt?.value) !== '4');
  }, [isBreakdownEvent, statusOptions]);

  const mergeCrewOptions = useCallback(
    (baseOptions, customOverride) => {
      const base = Array.isArray(baseOptions) ? baseOptions.filter(Boolean) : [];
      const customSource = Array.isArray(customOverride)
        ? customOverride
        : Array.isArray(customCrewOptions)
          ? customCrewOptions
          : [];
      const map = new Map();
      const addOption = (option) => {
        if (!option) return;
        if (option.value === undefined || option.value === null) return;
        if (option.value === ADD_CREW_MEMBER_OPTION.value) return;
        const key = String(option.value);
        map.set(key, { ...option });
      };
      base.forEach(addOption);
      customSource.forEach(addOption);
      const merged = Array.from(map.values());
      merged.sort((a, b) => (a?.label || '').localeCompare(b?.label || ''));
      return [ADD_CREW_MEMBER_OPTION, ...merged];
    },
    [customCrewOptions],
  );

  const firstAssignedWorkOrder = useMemo(() => {
    if (!Array.isArray(assignedWorkOrders) || assignedWorkOrders.length === 0) {
      return null;
    }
    if (editingEvent?.id) {
      const match = assignedWorkOrders.find(
        (wo) => String(wo.eventId) === String(editingEvent.id)
      );
      if (match) return match;
    }
    return assignedWorkOrders[0];
  }, [assignedWorkOrders, editingEvent?.id]);

  const normalizeCrewArray = useCallback((value) => {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry) => {
        if (entry === null || entry === undefined) return null;
        if (typeof entry === 'object') {
          if (entry.userId !== undefined && entry.userId !== null) {
            return entry.userId;
          }
          if (entry.id !== undefined && entry.id !== null) {
            return entry.id;
          }
        }
        return entry;
      })
      .filter((id) => id !== null && id !== undefined);
  }, []);

  const haveSameCrewMembers = useCallback((a, b) => {
    const normA = [...new Set(normalizeCrewArray(a))].sort();
    const normB = [...new Set(normalizeCrewArray(b))].sort();
    if (normA.length !== normB.length) return false;
    return normA.every((val, index) => val === normB[index]);
  }, [normalizeCrewArray]);

  const handleCrewCreationSuccess = useCallback(
    (result) => {
      const userId = result?.user?.id;
      if (!userId) {
        return;
      }
      const label = result?.user?.name || `Crew Member ${userId}`;
      const newOption = { value: userId, label, isCustom: true };

      setCustomCrewOptions((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        const filtered = existing.filter((opt) => opt?.value !== userId);
        const updated = [...filtered, newOption];
        setCrewOptions((current) => {
          const base = Array.isArray(current)
            ? current.filter(
                (opt) =>
                  opt &&
                  opt.value !== ADD_CREW_MEMBER_OPTION.value &&
                  !opt?.isCustom &&
                  opt.value !== userId,
              )
            : [];
          return mergeCrewOptions(base, updated);
        });
        return updated;
      });

      setNewEvent((prev) => {
        const prevData = prev && typeof prev === 'object' ? prev : {};
        const previousCrew = normalizeCrewArray(prevData.crew);
        if (previousCrew.includes(userId)) {
          return prevData;
        }
        return { ...prevData, crew: [...previousCrew, userId] };
      });
    },
    [mergeCrewOptions, normalizeCrewArray, setCrewOptions, setCustomCrewOptions, setNewEvent],
  );

  const registerEventCommentActions = useCallback((actions) => {
    eventCommentActionsRef.current = actions;
    return () => {
      if (eventCommentActionsRef.current === actions) {
        eventCommentActionsRef.current = null;
      }
    };
  }, []);

  const handleSave = useCallback(async () => {
    const validationResult = runDateValidations();
    if (Object.keys(validationResult).length > 0) {
      return;
    }

    const eventActions = eventCommentActionsRef.current;
    const shouldSubmitEventComment =
      eventActions &&
      typeof eventActions.hasPendingComment === 'function' &&
      eventActions.hasPendingComment();


    if (shouldSubmitEventComment && typeof eventActions.submit === 'function') {
      const submitResult = await eventActions.submit();
      if (submitResult === false) {
        return;
      }
    }

    if (typeof onSave === 'function') {
      onSave();
    }
  }, [onSave, runDateValidations]);

  const handleCrewFieldChange = useCallback(
    (values) => {
      const list = Array.isArray(values) ? values : [];
      const shouldOpenModal = list.includes(ADD_CREW_MEMBER_OPTION.value);
      const sanitized = [...new Set(list.filter((value) => value !== ADD_CREW_MEMBER_OPTION.value))];
      setNewEvent((prev) => {
        const prevData = prev && typeof prev === 'object' ? prev : {};
        return { ...prevData, crew: sanitized };
      });
      if (shouldOpenModal) {
        setShowCreateCrewModal(true);
      }
    },
    [setNewEvent, setShowCreateCrewModal],
  );

  useEffect(() => {
    if (!isBreakdownEvent) {
      return;
    }

    if (eventStatusId !== 4) {
      return;
    }

    setNewEvent((prev) => {
      const prevData = prev && typeof prev === 'object' ? prev : {};
      if (String(prevData.eventStatus) !== '4') {
        return prevData;
      }
      return { ...prevData, eventStatus: '' };
    });
  }, [eventStatusId, isBreakdownEvent, setNewEvent]);

  useEffect(() => {
    if (!isInProgressStatus) {
      return;
    }

    if (!show) {
      return;
    }

    if (newEvent?.startDate) {
      return;
    }

    const rounded = roundDateToNearestFiveMinutes();
    if (!rounded) {
      return;
    }

    const isoString = rounded.toISOString();
    setNewEvent((prev) => {
      const prevData = prev && typeof prev === 'object' ? prev : {};
      if (prevData.startDate) {
        return prevData;
      }
      return { ...prevData, startDate: isoString };
    });
  }, [isInProgressStatus, newEvent?.startDate, setNewEvent, show]);

  useEffect(() => {
    if (!isBreakdownEvent || !isInProgressStatus) {
      return;
    }

    const startValue = newEvent?.startDate;
    if (!startValue) {
      return;
    }

    if (newEvent?.plannedStartDate) {
      return;
    }

    setNewEvent((prev) => {
      const prevData = prev && typeof prev === 'object' ? prev : {};
      if (prevData.plannedStartDate) {
        return prevData;
      }
      return { ...prevData, plannedStartDate: startValue };
    });
  }, [isBreakdownEvent, isInProgressStatus, newEvent?.plannedStartDate, newEvent?.startDate, setNewEvent]);

  useEffect(() => {
    if (!eventTechsEnabled) {
      setCrewOptions([ADD_CREW_MEMBER_OPTION]);
      setCustomCrewOptions([]);
      setNewEvent((prev) => {
        const prevData = prev && typeof prev === 'object' ? prev : {};
        const prevCrew = normalizeCrewArray(prevData.crew);
        if (prevCrew.length === 0) return prevData;
        return { ...prevData, crew: [] };
      });
      return;
    }

    const workOrderCrew = normalizeCrewArray(firstAssignedWorkOrder?.crew || []);
    setNewEvent((prev) => {
      const prevData = prev && typeof prev === 'object' ? prev : {};
      if (haveSameCrewMembers(prevData.crew, workOrderCrew)) {
        return prevData;
      }
      return { ...prevData, crew: workOrderCrew };
    });
  }, [
    eventTechsEnabled,
    firstAssignedWorkOrder,
    haveSameCrewMembers,
    normalizeCrewArray,
    setCustomCrewOptions,
    setNewEvent,
  ]);

  useEffect(() => {
    if (!show) {
      setShowCreateCrewModal(false);
      setActiveTab('general');
    }
  }, [show]);

  useEffect(() => {
    if (!eventTechsEnabled) {
      setCrewOptions(mergeCrewOptions([]));
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const technicians = await getTechnicians();
        if (!isMounted) return;
        const baseOptions = (technicians || [])
          .map((tech) => {
            const value =
              tech?.userId !== undefined && tech?.userId !== null
                ? tech.userId
                : tech?.id ?? null;
            if (value === null) return null;
            const label =
              tech?.name ||
              tech?.users?.name ||
              `Technician ${value}`;
            return { value, label };
          })
          .filter(Boolean);
        setCrewOptions(mergeCrewOptions(baseOptions));
      } catch (err) {
        console.error('Error loading technicians', err);
        if (isMounted) setCrewOptions(mergeCrewOptions([]));
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [eventTechsEnabled, mergeCrewOptions, show]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const locations = await getEventLocations();
        if (!isMounted) return;
        const mapped = (locations || []).map((location) => ({
          value: location?.id ?? null,
          label: location?.name || `Location ${location?.id ?? ''}`.trim(),
        }));
        setEventLocationOptions(mapped);
      } catch (err) {
        console.error('Error loading event locations', err);
        if (isMounted) setEventLocationOptions([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!eventSystemEnabled) {
      setSystemOptions([]);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const systems = await getAssetSystems();
        if (!isMounted) return;
        const mapped = (systems || []).map((system) => {
          const code = system?.code ? String(system.code) : '';
          const name = system?.name ? String(system.name) : '';
          const label = [code, name].filter(Boolean).join(' - ');
          return {
            value: system?.id ?? null,
            label: label || `System ${system?.id ?? ''}`.trim(),
          };
        });
        setSystemOptions(mapped);
      } catch (err) {
        console.error('Error loading asset systems', err);
        if (isMounted) setSystemOptions([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [eventSystemEnabled]);

  useEffect(() => {
    if (!newEvent?.assetId) {
      setSelectedAssetOption(null);
      return;
    }

    setSelectedAssetOption((current) => {
      if (current && String(current.value) === String(newEvent.assetId)) {
        return current;
      }

      const fromOptions = assetOptions.find(
        (opt) => String(opt.value) === String(newEvent.assetId)
      );
      if (fromOptions) {
        return fromOptions;
      }

      const editingMatch =
        editingEvent && String(editingEvent.assetId) === String(newEvent.assetId)
          ? [
            editingEvent?.asset?.assetNumber || editingEvent?.assetNumber,
            editingEvent?.asset?.assetDescription || editingEvent?.assetDescription,
          ]
            .filter(Boolean)
            .join(' – ')
          : null;

      const label =
        newEvent?.assetLabel ||
        [newEvent?.assetNumber, newEvent?.assetDescription]
          .filter(Boolean)
          .join(' – ') ||
        editingMatch ||
        String(newEvent.assetId);

      return { value: newEvent.assetId, label };
    });
  }, [newEvent?.assetId, newEvent?.assetLabel, newEvent?.assetNumber, newEvent?.assetDescription, assetOptions, editingEvent]);

  const eventFields = useMemo(() => {

    /* EVENT BUSINESS RULES

    Pending (status 4)
    Event type not Breakdown
    • Show Planned Start and Planned End
    • Hide Start Date and End Date

    Event type Breakdown
    • Pending must not be selectable in the status dropdown

    In Progress (status 1)
    General
    • Start Date must be before Planned End Date

    Event type Breakdown
    • Show Start Date and default it to the current time rounded to five minutes
    • Hide Planned Start. If empty, auto fill with Start Date
    • Rename Planned End to Estimated End and show it after Start Date
    • Hide End Date

    Event type not Breakdown
    • Show Start Date and default it to the current time rounded to five minutes
    • Show Planned Start and Planned End
    • Hide End Date
    • Planned Start is not required

    Complete (status 2)
    Event type Breakdown
    • Show Start Date and End Date
    • Hide Planned Start and Planned End

    Event type not Breakdown
    • Show Start Date and End Date
    • Show Planned Start and Planned End
    • Planned Start and Planned End are optional

    Cancelled (status 3)
    • Show all date fields
    • No fields are required

    */
    
    const plannedEndLabel = isBreakdownEvent && isInProgressStatus ? 'Estimated End Date' : 'Planned End';

    let showPlannedStartDate = true;
    let showPlannedEndDate = true;
    let showStartDate = false;
    let showEndDate = false;

    if (isBreakdownEvent && isInProgressStatus) {
      showStartDate = true;
      showPlannedStartDate = false;
      showPlannedEndDate = true;
      showEndDate = false;
    } else if (isBreakdownEvent && isCompleteStatus && !isCancelledStatus) {
      showStartDate = true;
      showEndDate = true;
      showPlannedStartDate = false;
      showPlannedEndDate = false;
    } else {
      if (isInProgressStatus) {
        showStartDate = true;
      }
      if (isCompleteStatus) {
        showStartDate = true;
        showEndDate = true;
      }
      if (isPendingStatus) {
        showStartDate = false;
        showEndDate = false;
      }
    }

    if (!isBreakdownEvent && isInProgressStatus) {
      showPlannedStartDate = false;
    }

    if (isCancelledStatus) {
      showStartDate = true;
      showEndDate = true;
      showPlannedStartDate = true;
      showPlannedEndDate = true;
    }

    const plannedStartRequired = !(
      (isInProgressStatus && !isBreakdownEvent) ||
      (isCompleteStatus && !isBreakdownEvent) ||
      isCancelledStatus ||
      isCompleteStatus
    );

    const plannedEndRequired = !(
      (isCompleteStatus && !isBreakdownEvent) || isCancelledStatus || isCompleteStatus
    );

    const startRequired =
      (isInProgressStatus || isCompleteStatus) && !isCancelledStatus;

    const endRequired = isCompleteStatus && !isCancelledStatus;

    const fields = [
      { field: 'name', label: 'Event Name', type: 'text', size: 6, required: true },
      {
        field: 'assetId',
        label: 'Asset',
        type: 'paginated-select',
        size: 6,
        required: true,
        loadOptions: handleAssetSearch,
        selectedOption: selectedAssetOption,
        setSelectedOption: setSelectedAssetOption,
        options: assetOptions,
        isClearable: !disableAssetSelection,
        disabled: disableAssetSelection,
      },
      {
        field: 'eventTypeId',
        label: 'Event Type',
        type: 'select',
        size: 6,
        required: true,
        options: eventTypeOptions,
        isClearable: true,
      },
      {
        field: 'eventStatus',
        label: 'Status',
        type: 'select',
        size: 6,
        required: true,
        options: filteredStatusOptions,
        isClearable: true,
      },
    ];

    if (eventSystemEnabled) {
      fields.push({
        field: 'systemId',
        label: 'System',
        type: 'select',
        size: 6,
        options: systemOptions,
        isClearable: true,
      });
    }

    fields.push({
      field: 'eventLocationId',
      label: 'Event Location',
      type: 'select',
      size: 6,
      options: eventLocationOptions,
      isClearable: true,
    });

    if (showEventResponsibility) {
      fields.push({
        field: 'eventResponsibilityId',
        label: 'Event Responsibility',
        type: 'select',
        size: 6,
        options: eventResponsibilityOptions,
        isClearable: false,
      },{
        field: 'empty',
        label: '',
        type: 'general-spacer',
        size: 6
      });
    }

    const plannedStartField = {
      field: 'plannedStartDate',
      label: 'Planned Start',
      type: 'datetime',
      size: 6,
      required: plannedStartRequired,
    };

    const plannedEndField = {
      field: 'plannedEndDate',
      label: plannedEndLabel,
      type: 'datetime',
      size: 6,
      required: plannedEndRequired,
    };

    const startField = {
      field: 'startDate',
      label: 'Start Date',
      type: 'datetime',
      size: 6,
      required: startRequired,
    };

    const endField = {
      field: 'endDate',
      label: 'End Date',
      type: 'datetime',
      size: 6,
      required: endRequired,
    };

    const additionalFields = [];

    if (isBreakdownEvent && isInProgressStatus) {
      if (showStartDate) additionalFields.push(startField);
      if (showPlannedEndDate) additionalFields.push(plannedEndField);
    } else if (isBreakdownEvent && isCompleteStatus && !isCancelledStatus) {
      if (showStartDate) additionalFields.push(startField);
      if (showEndDate) additionalFields.push(endField);
    } else if (!isBreakdownEvent && isInProgressStatus) {
      if (showStartDate) additionalFields.push(startField);
      if (showPlannedStartDate) additionalFields.push(plannedStartField);
      if (showPlannedEndDate) additionalFields.push(plannedEndField);
      if (showEndDate) additionalFields.push(endField);
    } else {
      if (showPlannedStartDate) additionalFields.push(plannedStartField);
      if (showPlannedEndDate) additionalFields.push(plannedEndField);
      if (showStartDate) additionalFields.push(startField);
      if (showEndDate) additionalFields.push(endField);
    }

    fields.push(...additionalFields);

    if (eventTechsEnabled && firstAssignedWorkOrder) {
      fields.push({
        field: 'crew',
        label: 'Assigned Crew',
        type: 'multi-select',
        size: 12,
        required: false,
        options: crewOptions,
        onChange: handleCrewFieldChange,
      });
    }

    return fields;
  }, [
    assetOptions,
    crewOptions,
    eventLocationOptions,
    eventResponsibilityOptions,
    eventSystemEnabled,
    eventTechsEnabled,
    eventTypeOptions,
    filteredStatusOptions,
    firstAssignedWorkOrder,
    handleAssetSearch,
    handleCrewFieldChange,
    isBreakdownEvent,
    isCancelledStatus,
    isCompleteStatus,
    isInProgressStatus,
    isPendingStatus,
    selectedAssetOption,
    showEventResponsibility,
    systemOptions,
    disableAssetSelection,
  ]);

  const { renderField } = useFormBuilder(eventFields, newEvent, setNewEvent, combinedErrors);

  if (!show) return null;

  return (
    <>
      <CreateDisplayOnlyUserModal
        show={showCreateCrewModal}
        onClose={() => setShowCreateCrewModal(false)}
        onSuccess={handleCrewCreationSuccess}
      />
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content" style={{ minHeight: "700px" }}>
            <div className="modal-header">
              <h5 className="modal-title">{editingEvent ? 'Edit Event' : 'Add Event'}</h5>
              <button className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <ul className="nav bordered-tab border border-top-0 border-start-0 border-end-0 d-inline-flex nav-pills mb-16">
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                    onClick={() => setActiveTab('general')}
                  >
                    General
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${activeTab === 'comments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('comments')}
                  >
                    Comments
                  </button>
                </li>
              </ul>

            {activeTab === 'general' ? (
              <>
                <div className="row g-3">
                  {eventFields.map((field) => renderField(field))}
                </div>
                {showWorkOrderSection && (
              <>
                <div className="d-flex justify-content-between align-items-center my-16">
                  <div>
                    <strong>Event Work Orders</strong>
                  </div>
                  <div className="d-flex gap-2">
                    {onCompleteAllWork && editingEvent && (
                      <button className="btn btn-success btn-sm" onClick={onCompleteAllWork}>
                        Complete All Work
                      </button>
                    )}
                    {onOpenAddWorkOrder && editingEvent && isAssignableStatus && (
                      <button className="btn btn-primary btn-sm" onClick={onOpenAddWorkOrder}>
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" /> New Work Order
                      </button>
                    )}
                    {onOpenAssignModal && isAssignableStatus && !isCompleteStatus && !isCancelledStatus && (
                      <button className="btn btn-outline-primary btn-sm" onClick={onOpenAssignModal}>
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" /> Assign Work Order
                      </button>
                    )}
                  </div>
                </div>
                <div className="table-responsive event-workorders-table">
                  <table className="table bordered-table dataTable mb-3 w-100">
                    <thead>
                      <tr>
                        <th>Number</th>
                        <th>Title</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedWorkOrders.length === 0 && (
                        <tr>
                          <td colSpan="10" className="text-center py-4 rounded-0">
                            No Assigned Work Orders
                          </td>
                        </tr>
                      )}
                      {assignedWorkOrders.map((wo) => {
                        const priorityColour = wo.priority?.colours?.bootstrapName || 'secondary';
                        const statusLabel = wo.status?.name || '';
                        const statusColour = wo.status?.colours?.bootstrapName || 'secondary';
                        const isStatusEditable = ['in progress', 'complete'].includes(statusName || '');
                        return (
                          <tr key={wo.id}>
                            <td>{wo.workOrderNumber}</td>
                            <td>{wo.workOrderTitle}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span
                                  className={`d-inline-block rounded-1 bg-${priorityColour}`}
                                  style={{ width: '10px', height: '10px' }}
                                ></span>
                                {wo.priority?.name}
                              </div>
                            </td>
                            <td>
                              {isStatusEditable && onWorkOrderStatusChange ? (
                                <select
                                  className="form-select"
                                  value={wo.statusId || ''}
                                  onChange={(e) => onWorkOrderStatusChange(wo.id, e.target.value)}
                                >
                                  {workOrderStatuses.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  className={`bg-${statusColour}-focus text-${statusColour}-main px-16 py-2 rounded-pill fw-medium text-sm`}
                                >
                                  {statusLabel}
                                </span>
                              )}
                            </td>
                            <td>
                              {onEditWorkOrder && (
                                <button
                                  className="edit-btn w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center mx-6"
                                  onClick={() => onEditWorkOrder(wo)}
                                >
                                  <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                                </button>
                              )}
                              {onDeleteWorkOrder && (
                                <button
                                  className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                  onClick={() => onDeleteWorkOrder(wo.id)}
                                >
                                  <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {((editingEvent && statusName && ['in progress', 'complete'].includes(statusName)) || !editingEvent) && (
              <>
                <div className="d-flex justify-content-between align-items-center my-16">
                  <div>
                    <strong>Delay Codes</strong>
                  </div>
                  <div>
                    {onAddDelay && (
                      <button className="btn btn-primary btn-sm" onClick={onAddDelay}>
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" /> Add Delay
                      </button>
                    )}
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table bordered-table dataTable mb-3 w-100">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Name</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventDelays.length === 0 && (
                        <tr>
                          <td colSpan="10" className="text-center py-4 rounded-0">
                            No Delays Recorded
                          </td>
                        </tr>
                      )}
                      {eventDelays.map((d) => (
                        <tr key={d.id}>
                          <td>{d.eventDelayCode?.code}</td>
                          <td>{d.eventDelayCode?.name}</td>
                          <td>{formatDateTime ? formatDateTime(d.delayStartDate) : d.delayStartDate}</td>
                          <td>{formatDateTime ? formatDateTime(d.delayEndDate) : d.delayEndDate}</td>
                          <td>
                            {onEditDelay && (
                              <button
                                className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                onClick={() => onEditDelay(d)}
                              >
                                <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                              </button>
                            )}
                            {onDeleteDelay && (
                              <button
                                className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                onClick={() => onDeleteDelay(d.id)}
                              >
                                <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
              </>
            ) : (
              <>
                <div className="mt-4" style={{ justifyContent: 'center', display: 'flex' }}>
                  <EventCommentsSection
                    eventId={editingEvent?.id}
                    localComments={eventComments}
                    onLocalCommentsChange={onEventCommentsChange}
                    onCommentActions={registerEventCommentActions}
                  />
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export default EventEditModal;
